import {Action, ActionHackCtx, ActionMenu, ActionMenuDep, IAction, IActionToggle} from "/@lib@/commons/actions.js";
import {Desk} from "/@lib@/commons/desk.js";
import {IReg, IRegPointer, REG} from "/@lib@/commons/registry.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IBasicUniversePointer, IUniverseEnv} from "/@lib@/core/universe.js";
import "/@back@/core/plugins/plugins_Perms.js";
import "/@lib@/commons/utils/user_Perms.js";
import {EventMgr} from "/@lib@/commons/events.js";
import {OAboutDialogInit} from "/@back@/core/dialogs/about.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {BASIS} from "/@back@/commons/basis.js";
import {JLastDatas, LASTDATAS} from "/@lib@/commons/lastDatas.js";
import {LastDatasDeskFeat} from "/@lib@/core/lastDatas.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {CheckPrefArea, SelectPrefArea, UserPrefPageConfig} from "/@back@/core/dialogs/userPrefEditorConfig.js";

/**
 * Ajoute le menu Options au bandeau avec deux entrées par défaut :
 * - gestion des users;
 * - A propos de l'application
 *
 */

export interface OOptionsPlugins {
	docUrl?: string
	about?: OAboutDialogInit
}

export function initApp(reg: IReg<IUniverseEnv> = REG.reg, options?: OOptionsPlugins) {

	class WrapReg extends ActionHackCtx<Element, IReg<IUniverseEnv>> {
		constructor(sub: IAction<IReg<IUniverseEnv>>, public reg: IReg<IUniverseEnv>) {
			super(sub);
		}

		wrapCtx(ctx: Element): IReg<IUniverseEnv> {return this.reg}

		initButtonNode(buttonNode: Element, ctx: Element) {
			//Affectation du bon registre dans le contexte graphique.
			(buttonNode as IRegPointer<IUniverseEnv>).reg = this.reg;
			this.sub.initButtonNode(buttonNode, this.reg);
		}
	}

	function wrap(action: IAction<IReg<IUniverseEnv>>): IAction<Element> {
		return new WrapReg(action, reg);
	}

	REG.reg.addToList("appframe:header:toolbar", "params", 1, wrap(new ActionMenuDep<IReg<IUniverseEnv>>()
			.setLabel("Gestion")
			//.setIcon("/@skin@/commons/icons/options.svg")
			.setSkinOver("appframeHeader_options")
			.setActionLists("appframe:header:toolbar:params")
			.setGroupOrder("ui * users admin about")
			.requireVisiblePerm("ui.optionsPlg")),
		100);
	REG.reg.addToList("appframe:header:toolbar", "help", 1, wrap(new ActionMenuDep<IReg<IUniverseEnv>>()
			.setLabel("Affichage")
			//.setIcon("/@skin@/commons/icons/options.svg")
			.setGroupOrder("* about")
			.setActionLists("appframe:header:toolbar:options")),
		1000);

	if (options && options.docUrl) {
		REG.reg.addToList("appframe:header:toolbar:options", "scenari", 1, new Action().setLabel("Documentation...").setGroup("about").override("execute", () => {
			window.open(options.docUrl, "_blank");
		}), 950);
	}

	REG.reg.addToList("appframe:header:toolbar:params", "admin", 1, wrap(new ActionMenuDep()
		.setLabel("Administration système").setGroup("admin")
		.setActionLists("appframe:header:toolbar:params:admin")), 950);

	REG.reg.addToList("appframe:header:toolbar:params:admin", "notifUsers", 1, wrap(new Action<IReg<IUniverseEnv>>("notifUsers")
		.setLabel("Notifications pour maintenance")
		.requireVisiblePerm("ui.optionsPlg.notif")
		.setVisible((ctx: IReg<IUniverseEnv>, action: Action) =>
			!ctx.reg.env.universe.auth.config.noAuthentication
			&& action.checkObjectRootVisiblePerm(ctx)
			&& (ctx.isListFilled("plg:notifUsers:universes") || ctx.env.universe.config.liaiseUrl != null)
			&& !ctx.env.universe.config.isDesktopApp
		)
		.setExecute(async function (this: Action, ctx: IReg<IUniverseEnv>, event) {
			(await import("/@back@/core/dialogs/notifUsers.js")).openNotifUsers({regs: ctx.isListFilled("plg:notifUsers:universes") ? (ctx.getList("plg:notifUsers:universes") as IRegPointer<IUniverseEnv>[]).map(elt => elt.reg) : [ctx]});
		})
	), 10);

	REG.reg.addToList("appframe:header:toolbar:options", "about", 1, wrap(new Action<IReg<IUniverseEnv>>("about").setLabel("À propos...").setGroup("about").setExecute(async function (reg: IReg<IUniverseEnv>) {
		await import("/@back@/core/dialogs/about.js");
		POPUP.confirm(<c-about î={BASIS.newInit(options ? options.about : null, reg)}/>, document.body, {
			kind: null,
			titleBar: {barLabel: {label: "À propos"}},
			resizer: {},
			cancelLbl: null
		});
	})), 1000);

	if(reg.env.universe.auth?.config.noAuthentication) {
		reg.addToList("appframe:header:toolbar:options", "prefs", 1, new Action<Element>('prefs')
			.setLabel("Préférences...")
			.setGroup("ui")
			.setExecute(async (ctx: Element) => {
				const reg = REG.findReg(ctx) as IReg<IBasicUniversePointer>;
				const {UserPrefEditor} = await import("/@back@/core/dialogs/userPrefEditor.js");
				const body = new UserPrefEditor().initialize({reg, prefEditorsList: "user:prefs"});
				await body.initializedAsync;
				POPUP.showDialog(body, ctx, {titleBar: "Préférences", resizer:{}});
			}));
	}

}


/**
 * Feature du desk pour gérer le thème graphique :
 * - Ajoute une entrée dans le menu Options (ou logged-user) pour choisir le thème
 * - Gère la persistance du choix dans les persistUserDatas.
 * - déclare par défaut 2 thèmes, un clair et un foncé.
 */
export class UiThemeDeskFeat extends Desk {

	private static CORE_THEMES: { id: string, styleSheetPath: string, label: string }[] = [
		{id: "light", styleSheetPath: "/@skin@/core/themes/light-bluegrey.css", label: "Thème gris-bleu"},
		{id: 'light-contrast', styleSheetPath: "/@skin@/core/themes/light-contrast-bluegrey.css", label: "Thème gris-bleu contrasté"},
		{id: 'light-sand', styleSheetPath: "/@skin@/core/themes/light-sand.css", label: "Thème sable-rouille"},
		{id: 'light-pinkturquoise', styleSheetPath: "/@skin@/core/themes/light-pinkturquoise.css", label: "Thème rose-turquoise"},
		{id: 'light-contrast-greenblue', styleSheetPath: "/@skin@/core/themes/light-contrast-greenblue.css", label: "Thème vert-bleu"},
		{id: 'light-contrast-yellowpink', styleSheetPath: "/@skin@/core/themes/light-contrast-yellowpink.css", label: "Thème rose-moutarde"},
		{id: 'dark', styleSheetPath: "/@skin@/core/themes/dark.css", label: "Thème sombre noir-bleu"},
		{id: 'dark-greenblue', styleSheetPath: "/@skin@/core/themes/dark-greenblue.css", label: "Thème sombre vert-bleu"},
		{id: 'dark-greygreen', styleSheetPath: "/@skin@/core/themes/dark-greygreen.css", label: "Thème sombre gris-vert"}
	]

	static add(desk: Desk, themes?: ThemeAction[], reg: IReg<IBasicUniversePointer> = REG.reg): UiThemeDeskFeat {
		if (this.isIn(desk)) return desk;
		const d = desk as UiThemeDeskFeat;
		desk.assignProps(this);
		d.themes = themes || this.registerCoreThemes([]);
		// fallback : un thème est requis
		if (d.themes.length === 0) this.registerCoreThemes(d.themes, /^(light|dark)$/);

		d.onThemeChange = new EventMgr();

		reg.addToList("appframe:header:toolbar:options", "uiTheme", 1, new ThemeMenu(d.themes));
		// reg.addToList("appframe:header:toolbar:options", "uiTheme", 1, new ThemeMenu(d.themes).setVisible(() => !auth || !auth.currentAuthenticatedUser));
		// reg.addToList("actions:loggedUser", "uiTheme", 1, new ThemeMenu(d.themes).setGroup("me"), 4);

		const auth = reg.env.universe?.auth;

		const userPrefs = reg.env.universe.userDatas;
		if (userPrefs) {
			reg.addToList(UserPrefPageConfig.LIST_userPrefsFrontPage, "uiTheme", 1, new SelectPrefArea("uiTheme")
				.setLabel("Thème par défaut")
				.setDescription("Ce thème sera exploité lors de l'ouverture de nouvelles fenêtres")
				.setBuildOptions((selectElt: HTMLSelectElement, currentVal: string | null) => {
					for (const s of d.themes) {
						const value = s.getId();
						selectElt.appendChild(<option value={value} selected={value === currentVal ? "" : undefined} label={s.getLabel(null)}/>);
					}
					selectElt.appendChild(<option value="" selected={!currentVal ? "" : undefined} label="Non spécifié"/>);
				})
			);

			reg.addToList(UserPrefPageConfig.LIST_userPrefsFrontPage, "gridSimpleClickPrefered", 1, new CheckPrefArea("gridSimpleClickPrefered")
					.setLabel("Privilégier le simple click au double-click")
					.setDescription("Force le comportement de l'application comme sur une tablette, même si une souris est disponible")
					.setReloadAppNeeded()
				, 50);
		}

		REG.reg.addToList(Desk.LC_init, 'uiTheme', 1, () => {
			const ldDesk = LastDatasDeskFeat.isIn(desk) ? desk : null;
			if (ldDesk) LASTDATAS.buildLastDatasHooks.add((datas: JLDTheme) => {
				datas.uiTheme = d.currentTheme?.getId();
			});
			//Recherche du AuthServer
			d.setThemeById((ldDesk?.lastDatas as JLDTheme)?.uiTheme || reg.getUserData("uiTheme"));
			auth?.listeners.on("loggedUserChanged", () => {
				d.setThemeById((ldDesk?.lastDatas as JLDTheme)?.uiTheme || reg.getUserData("uiTheme"));
			});
		});
		return d;
	}

	/** Ajoute les skin "core" au registre des thèmes passé en paramètre
	 * 	Si @selector est non spécifié, alors TOUS les thèmes sont injectés
	 * 	Gestion de l'unicité des thèmes dans le registre  */
	static registerCoreThemes(themes: ThemeAction[], selector?: RegExp): ThemeAction[] {
		this.CORE_THEMES.forEach((coreTheme) => {
			if ((!selector || coreTheme.id.match(selector)) && !themes.find((act) => act.getId() === coreTheme.id))
				themes.push(new ThemeAction(coreTheme.id, coreTheme.styleSheetPath).setLabel(coreTheme.label))
		});
		return themes;
	}

	static isIn(desk: Desk): desk is UiThemeDeskFeat {return 'setThemeById' in desk}

	/**
	 * Fonction à déclarer sur un HTMLElementIFrame :
	 * if (UiThemeDeskFeat.isIn(desk)) (iframe as any).injectTheme = UiThemeDeskFeat.injectTheme;
	 *
	 * Pour ensuite pouvoir être appelée par le js de l'iframe :
	 * if(frameElement?.injectTheme) frameElement.injectTheme();
	 */
	static injectTheme(this: HTMLIFrameElement) {
		if (UiThemeDeskFeat.isIn(desk)) desk.injectThemeInSubFrame(this);
		if (FontSizeDeskFeat.isIn(desk)) desk.injectFontSizeInSubFrame(this);
	}

	themes: ThemeAction[];
	currentTheme: ThemeAction | null;
	onThemeChange: EventMgr<(theme: ThemeAction) => void>;

	setThemeById(id: string) {
		const theme = id && this.themes.find((t) => t.getId() === id) || this.getDefaultTheme();
		if (!theme || theme === this.currentTheme) return;
		theme.setAsCurrentTheme();
	}

	getDefaultTheme(): ThemeAction {
		if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
			const th = this.themes.find((t) => t.isDark);
			if (th) return th;
		}
		return this.themes[0];
	}

	injectThemeInSubFrame(frame: HTMLIFrameElement) {
		const subDoc = frame.contentDocument;
		const style = subDoc.createElement("link");
		style.setAttribute("rel", "stylesheet");
		style.setAttribute("type", "text/css");
		subDoc.head.appendChild(style);

		const refreshTheme = () => {
			DOM.setAttr(style, "href", this.currentTheme && this.currentTheme.styleSheetPath || "");
		};

		refreshTheme();
		this.onThemeChange.add(refreshTheme);
		subDoc.addEventListener("visibilitychange", () => {
			if (subDoc.visibilityState === "visible") {
				refreshTheme();
				this.onThemeChange.add(refreshTheme);
			} else {
				this.onThemeChange.delete(refreshTheme);
			}
		});
	}

}

interface JLDTheme extends JLastDatas {
	uiTheme?: string
}

class ThemeMenu extends ActionMenu<Element> {
	constructor(themes: ThemeAction[]) {
		super('theme');
		this._label = "Thème graphique";
		this._group = "ui";
		this._actions = themes;
	}

	isVisible(ctx: Element): boolean {
		if (this._actions.length <= 1) return false;
		return super.isVisible(ctx);
	}
}

export class ThemeAction extends Action<Element> implements IActionToggle<Element> {
	constructor(id: string, public styleSheetPath: string | null) {
		super(id);
	}

	get isDark() {return this._id.indexOf('dark') >= 0}

	setAsCurrentTheme(cb?: (done: boolean) => void) {
		const uiTheme = document.getElementById("uiTheme") as HTMLLinkElement;
		if (uiTheme) uiTheme.remove();

		const themeChanged = () => {
			if (UiThemeDeskFeat.isIn(desk)) {
				desk.currentTheme = this;
				desk.onThemeChange.emitCatched(this);
			}
			if (cb) cb(true);
		};

		if (this.styleSheetPath) {
			const linkElem = document.head.appendChild(<link id="uiTheme" rel="stylesheet" type="text/css" href={this.styleSheetPath} path={this.styleSheetPath}/>);
			linkElem.addEventListener('load', themeChanged);
			linkElem.addEventListener('error', () => {
				if (uiTheme) document.head.appendChild(uiTheme);
				if (cb) cb(false);
			});
		} else {
			themeChanged();
		}
	}


	isToggle(ctx: Element): this is IActionToggle<Element> {return true}

	getDatas(api: 'toggle', ctx: Element): boolean | null {
		const uiTheme = document.getElementById("uiTheme") as HTMLLinkElement;
		return this.styleSheetPath ? uiTheme !== null && uiTheme.getAttribute("path") === this.styleSheetPath : uiTheme === null;
	}

	execute(ctx: Element, ev?: Event): any | 'noPreventDefault' {
		this.setAsCurrentTheme(
			//Passé en écran de pref explicite
			// (done) => {
			// 	const persistUS = done ? REG.findReg(ctx).getPersistUserStates() : null;
			// 	if (persistUS) persistUS.setUserDatas({uiTheme: this.getId()});
			// }
		)
	}

	initButtonNode(buttonNode: HTMLElement, ctx: Element): void {
		if (this._id === REG.findReg(ctx).getUserData("uiTheme")) (buttonNode as Button).label += " (par défaut)";
		buttonNode.style.setProperty("--icon-size", "0");
	}
}

/**
 * Feature du desk pour gérer les langues :
 * - Ajoute une entrée dans le menu Options pour choisir la langue
 * - Gère la persistance du choix dans les persistUserDatas.
 */
export class UiLangDeskFeat extends Desk {

	/**
	 *
	 * @param desk
	 * @param currentLangCode
	 * @param langs
	 * @param reg
	 */
	static add(desk: Desk, currentLangCode: string, langs: LangAction[], reg: IReg<IBasicUniversePointer> = REG.reg): UiLangDeskFeat {
		if (this.isIn(desk)) return desk;
		const d = desk as UiLangDeskFeat;
		desk.assignProps(this);
		d.langs = langs;
		d.currentLang = langs.find((l) => l.getId() == currentLangCode) || langs[0];

		reg.addToList("appframe:header:toolbar:options", "uiLang", 1, d.newLangMenu());
		// const auth = reg.env.universe?.auth;
		// reg.addToList("appframe:header:toolbar:options", "uiLang", 1, d.newLangMenu().setVisible(() => !auth || !auth.currentAuthenticatedUser));
		// reg.addToList("actions:loggedUser", "uiLang", 1, d.newLangMenu().setGroup("me"), 6);
		/*
		if(d.autoSwitch && auth) {
			REG.reg.addToList(Desk.LC_init, 'uiLang', 1, () => {
				if (auth.currentAuthenticatedUser) d.setLangByCode(reg.getUserData("uiLang"));
					auth.listeners.on("loggedUserChanged", () => {
					d.setLangByCode(reg.getUserData("uiLang"));
				});
			});
		}
		*/
		return d;
	}

	static isIn(desk: Desk): desk is UiLangDeskFeat {return 'setLangByCode' in desk}

	//autoSwitch : boolean = false;
	langs: LangAction[];
	currentLang: LangAction;

	newLangMenu(): ActionMenu<Element> {return new LangMenu(this.langs).setIcon("/@skin@/core/actions/options/lang.svg")}

	setLangByCode(code: string) {
		const lang = code && this.langs.find((t) => t.getId() === code) || this.getDefaultLang();
		if (!lang || lang === this.currentLang) return;
		lang.execute(document.documentElement);
	}

	getDefaultLang(): LangAction {
		return this.langs[0];
	}

}

class LangMenu extends ActionMenu<Element> {
	constructor(langs: LangAction[]) {
		super('lang');
		this._label = "Langue";
		this._group = "ui";
		this._actions = langs;
	}
}

export class LangAction extends Action<Element> implements IActionToggle<Element> {
	constructor(id: string, public frontUrl: string) {
		super(id);
	}

	isToggle(ctx: Element): this is IActionToggle<Element> {return true}

	getDatas(api: 'toggle', ctx: Element): boolean | null {
		const d = desk as UiLangDeskFeat;
		return this === d.currentLang;
	}

	execute(ctx: Element, ev?: Event): any | 'noPreventDefault' {
		if (UiLangDeskFeat.isIn(desk) && desk.currentLang != this) {
			desk.currentLang = this;
			document.location.assign(this.frontUrl);
			const d = desk as UiLangDeskFeat;
			/*if(d.autoSwitch) {
				let persistUS = REG.findReg(ctx).getPersistUserStates();
				if (persistUS) persistUS.setUserDatas({uiLang: this.getId()});
			}*/
		}

	}
}


/**
 * Feature du desk pour gérer le changement de taille de police :
 * - Ajoute une entrée dans le menu Options (ou logged-user) pour choisir la taille de police
 * - Gère la persistance du choix dans les persistUserDatas.
 */
export class FontSizeDeskFeat extends Desk {

	static add(desk: Desk, reg: IReg<IBasicUniversePointer> = REG.reg): FontSizeDeskFeat {
		if (this.isIn(desk)) return desk;
		const d = desk as FontSizeDeskFeat;
		desk.assignProps(this);

		const sizes: FontSizeAction[] = [];
		for (let size = 6; size <= 20; size += 1) {
			sizes.push(new FontSizeAction(size));
		}
		d.onFontSizeChange = new EventMgr();

		const auth = reg.env.universe?.auth;
		reg.addToList("appframe:header:toolbar:options", "fontSize", 1, new FontSizeMenu(sizes));
		// reg.addToList("appframe:header:toolbar:options", "fontSize", 1, new FontSizeMenu(sizes).setVisible(() => !auth || !auth.currentAuthenticatedUser));
		// reg.addToList("actions:loggedUser", "fontSize", 1, new FontSizeMenu(sizes).setGroup("me"), 5);

		const userPrefs = reg.env.universe.userDatas;
		if (userPrefs) {
			reg.addToList(UserPrefPageConfig.LIST_userPrefsFrontPage, "fontSize", 1, new SelectPrefArea("fontSize")
				.setLabel("Taille de police par défaut")
				.setDescription("Cette taille sera exploitée lors de l'ouverture de nouvelles fenêtres")
				.setBuildOptions((selectElt: HTMLSelectElement, currentVal: string | null) => {
					for (const s of sizes) {
						const value = s.getLabel(null);
						selectElt.appendChild(<option value={value} selected={value === currentVal ? "" : undefined} label={value}/>);
					}
					selectElt.appendChild(<option value="" selected={!currentVal ? "" : undefined} label="Non spécifiée"/>);
				})
			);
		}

		REG.reg.addToList(Desk.LC_init, 'fontSize', 1, () => {
			const ldDesk = LastDatasDeskFeat.isIn(desk) ? desk : null;
			if (ldDesk) LASTDATAS.buildLastDatasHooks.add((datas: JLDFontSize) => {
				datas.fontSize = d.currentFontSize;
			});
			const fontSize = (ldDesk?.lastDatas as JLDFontSize)?.fontSize || reg.getUserData("fontSize") as number;
			if (fontSize) d.setFontSize(fontSize);
			else {
				document.documentElement.style.fontSize = '';
				if (!d.currentFontSize) d.currentFontSize = FontSizeAction.getComputedFontSize();
			}
			auth?.listeners.on("loggedUserChanged", () => {
				const fontSize = (ldDesk?.lastDatas as JLDFontSize)?.fontSize || reg.getUserData("fontSize") as number;
				if (fontSize) d.setFontSize(fontSize);
				else {
					document.documentElement.style.fontSize = '';
					if (!d.currentFontSize) d.currentFontSize = FontSizeAction.getComputedFontSize();
				}
			});
		});
		return d;
	}

	static isIn(desk: Desk): desk is FontSizeDeskFeat {return 'setFontSize' in desk}

	onFontSizeChange: EventMgr<(size: string) => void>;

	currentFontSize: number;

	get currentFontSizeCss(): string {return this.currentFontSize + 'pt'}

	setFontSize(size: number) {
		if (this.currentFontSize === size) return;
		this.currentFontSize = size;
		const s = this.currentFontSizeCss;
		document.documentElement.style.fontSize = s;
		this.onFontSizeChange.emitCatched(s);
	}

	injectFontSizeInSubFrame(frame: HTMLIFrameElement) {
		const subDoc = frame.contentDocument;
		const refreshFontSize = (size: string) => {
			subDoc.documentElement.style.fontSize = size;
		};
		refreshFontSize(this.currentFontSizeCss);
		this.onFontSizeChange.add(refreshFontSize);
		subDoc.addEventListener("visibilitychange", () => {
			if (subDoc.visibilityState === "visible") {
				refreshFontSize(this.currentFontSizeCss);
				this.onFontSizeChange.add(refreshFontSize);
			} else {
				this.onFontSizeChange.delete(refreshFontSize);
			}
		});
	}
}

interface JLDFontSize extends JLastDatas {
	fontSize?: number
}

class FontSizeMenu extends ActionMenu<Element> {
	constructor(sizes: FontSizeAction[]) {
		super('fontSize');
		this._label = "Taille de police";
		this._group = "ui";
		this._actions = sizes;
	}
}

export class FontSizeAction extends Action<Element> implements IActionToggle<Element> {
	constructor(protected _size: number) {
		super('fontSize');
		this._label = _size.toString();
		this._group = "ui";
	}

	isToggle(ctx: Element): this is IActionToggle<Element> {return true}

	getDatas(api: 'toggle', ctx: Element): boolean | null {
		const fontSize = FontSizeDeskFeat.isIn(desk) ? desk.currentFontSize : FontSizeAction.getComputedFontSize();
		return fontSize === this._size;
	}

	execute(ctx: Element, ev?: Event): any | 'noPreventDefault' {
		if (FontSizeDeskFeat.isIn(desk)) desk.setFontSize(this._size);
		else document.documentElement.style.fontSize = this._size + 'pt';
		//Passé en écran de pref explicite
		// const persistUS = REG.findReg(ctx).getPersistUserStates();
		// if (persistUS) persistUS.setUserDatas({fontSize: this._size});
	}

	initButtonNode(buttonNode: HTMLElement, ctx: Element): void {
		if (this._size == REG.findReg(ctx).getUserData("fontSize")) (buttonNode as Button).label += " (par défaut)";
		buttonNode.style.setProperty("--icon-size", "0");
	}

	static getComputedFontSize(): number {
		const pxFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
		return Math.round(pxFontSize * 3 / 4 * 100) / 100;
	}
}